import { useState } from 'react';

interface DocumentViewerProps {
  documentUrl?: string;
  documentName?: string;
}

export default function DocumentViewer({
  documentUrl = '#',
  documentName = 'Invoice-Document.pdf',
}: DocumentViewerProps) {
  // Starts collapsed by default per user requirement
  const [isOpen, setIsOpen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  const isValidUrl = documentUrl && documentUrl !== '#';

  return (
    <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-card overflow-hidden mb-6">
      {/* Header with Toggle */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-brand-bg/50 dark:hover:bg-strokedark/30 transition-colors text-left cursor-pointer"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-2xl bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 flex items-center justify-center flex-shrink-0 border border-brand-vib/20">
            <svg className="w-5 h-5 fill-current" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <div className="text-[10.5px] font-mono font-bold uppercase tracking-wider text-brand-vib dark:text-brand-light">
              ORIGINAL INVOICE DOCUMENT
            </div>
            <p className="text-xs sm:text-sm text-brand-navy dark:text-white mt-0.5">
              {isOpen ? 'Click to collapse document viewer.' : 'Click to view the embedded document inline — or download / open in new tab.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 pl-2">
          <span className="text-xs font-mono font-semibold text-brand-soft hidden sm:inline-block">
            {isOpen ? 'Hide' : 'Show Preview'}
          </span>
          <svg
            className={`w-5 h-5 text-brand-soft transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Collapsible Document Container */}
      {isOpen && (
        <div className="border-t border-brand-line dark:border-strokedark p-3 sm:p-5 bg-brand-bg/40 dark:bg-boxdark-2/40">
          {/* Action & Status Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 p-3 rounded-xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-xs">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 uppercase tracking-wider shrink-0 border border-brand-vib/20">
                PDF Document
              </span>
              <span className="text-xs sm:text-sm font-semibold font-mono text-brand-navy dark:text-white truncate">
                {documentName}
              </span>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <a
                href={documentUrl}
                target="_blank"
                rel="noreferrer"
                className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors shadow-xs inline-flex items-center gap-1.5"
              >
                <span>Open in new tab</span>
                <svg className="w-3 h-3 fill-current" viewBox="0 0 20 20">
                  <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                  <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                </svg>
              </a>

              <a
                href={documentUrl}
                download={documentName}
                className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-strokedark text-brand-navy dark:text-white border border-brand-line dark:border-strokedark hover:bg-brand-bg transition-colors inline-flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5 fill-current text-brand-soft" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>Download</span>
              </a>
            </div>
          </div>

          {/* Embedded Document View Container */}
          <div className="relative w-full h-[540px] sm:h-[720px] rounded-xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-inner overflow-hidden flex flex-col items-center justify-center">
            {isValidUrl && !iframeError ? (
              <>
                {!iframeLoaded && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90 dark:bg-boxdark/90 backdrop-blur-xs text-xs font-mono text-brand-soft">
                    <div className="w-7 h-7 border-2 border-brand-vib border-t-transparent rounded-full animate-spin mb-2" />
                    <span>Loading original invoice document…</span>
                  </div>
                )}
                <iframe
                  src={documentUrl}
                  title={documentName}
                  onLoad={() => setIframeLoaded(true)}
                  onError={() => setIframeError(true)}
                  className="w-full h-full border-0 bg-white"
                />
              </>
            ) : (
              <div className="p-8 text-center max-w-md">
                <div className="w-14 h-14 rounded-2xl bg-brand-blue-tint dark:bg-brand-900/40 text-brand-vib flex items-center justify-center mx-auto mb-3 border border-brand-vib/20">
                  <svg className="w-7 h-7 fill-current" viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10H7v-2h10v2zm0-4H7V7h10v2z" />
                  </svg>
                </div>
                <p className="text-sm font-bold text-brand-navy dark:text-white">
                  {documentName}
                </p>
                <p className="text-xs text-brand-muted dark:text-bodydark mt-1 mb-4">
                  Original invoice file is ready. Click below to inspect in a new window or download directly.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <a
                    href={documentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors shadow-xs"
                  >
                    Open in new tab ↗
                  </a>
                  <a
                    href={documentUrl}
                    download={documentName}
                    className="px-4 py-2 rounded-xl text-xs font-semibold bg-white dark:bg-strokedark text-brand-navy dark:text-white border border-brand-line dark:border-strokedark hover:bg-brand-bg transition-colors"
                  >
                    Download PDF
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
